import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from 'react';

import { useAlert } from 'contexts/alert/useAlert';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Container } from 'contexts/datamodel/DataModelContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  ArticleItem,
  ContentUnion,
  MobileArticleItem,
  ModuleContainer,
  MultiZoneModuleContainer
} from 'data/generated/graphql';
import { MULTIZONE_MODULE } from 'utils/constants';
import { adjustContentIndexes, compareItems, replaceZoneIdentifiers } from 'utils/moduleItems';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

export interface StashedRegularModuleSnapshot {
  kind: 'regular';
  moduleHierarchyId: string;
  treatmentType: string;
  items: ArticleItem[];
}

export interface StashedMultiZoneModuleSnapshot {
  kind: 'multizone';
  moduleHierarchyId: string;
  treatmentType: string;
  collection: ArticleItem[];
  zones: Array<{ name: string; contentIndexes: number[] }>;
}

export type StashedModuleSnapshot = StashedRegularModuleSnapshot | StashedMultiZoneModuleSnapshot;

const getStashedItems = (m: StashedModuleSnapshot): ArticleItem[] => (m.kind === 'regular' ? m.items : m.collection);

export interface StashedGroupEntry {
  groupHierarchyId: string;
  groupId: string;
  groupLabel: string;
  childHierarchyIds: string[];
  modules: StashedModuleSnapshot[];
}

interface ScratchpadContextProps {
  height: number;
  setHeight: Dispatch<SetStateAction<number>>;
  scrollTop: number;
  setScrollTop: Dispatch<SetStateAction<number>>;
  scratchpad: ContentUnion[];
  addItemToScratchpad: (destHierarchyId: string, item: ContentUnion) => void;
  removeItemFromScratchpad: (item: ContentUnion) => void;
  addItemToScratchpadFromDraft: (sourceItemHierarchyId: string, destHierarchyId: string) => void;
  swapItemsInScratchpad: (srcIndex: number, destIndex: number) => void;
  getAllScratchpadItemIds: () => string[];
  getScratchpad: (deleteResolvedFields: boolean) => ContentUnion[];
  stashGroupItems: (entry: StashedGroupEntry) => void;
  restoreLastStashedGroup: () => void;
  getLastStashedGroup: () => StashedGroupEntry | null;
}

const ScratchpadContext = createContext<ScratchpadContextProps | null>(null);

export const useScratchpadContext = () => {
  const context = useContext(ScratchpadContext);
  if (!context) {
    return null;
  }
  return context;
};

const INITIAL_SCRATCHPAD_HEIGHT = 150;

interface Props {
  scratchpad?: ContentUnion[] | undefined | null;
}

export const ScratchpadProvider: FC<PropsWithChildren<Props>> = ({ children, scratchpad }) => {
  const [height, setHeight] = useState(INITIAL_SCRATCHPAD_HEIGHT);
  const [scrollTop, setScrollTop] = useState(0);
  const {
    readHierarchyId,
    getEntity,
    setNewRoot,
    removeEntity,
    getRootCopy,
    modifyEntity,
    setHasModelChangedOnce,
    setHasModelChangedRecently
  } = useDataModelContext();
  const [scratchpadState, setScratchpadState] = useState<ContentUnion[]>(() => scratchpad ?? []);
  const [lastStashedGroup, setLastStashedGroup] = useState<StashedGroupEntry | null>(null);
  const { alertError } = useAlert();

  useEffect(() => {
    if (scratchpad) {
      setScratchpadState(scratchpad);
    }
  }, [scratchpad]);

  function addItemToScratchpad(destHierarchyId: string, item: ContentUnion) {
    const destHierarchyIdSanitized = destHierarchyId.replace(/.+?#/, '');
    const destHierarchyIdIndices = readHierarchyId(destHierarchyIdSanitized, true);
    const lastDestIndex = destHierarchyIdIndices.pop();

    if (scratchpadState.some((scratchpadItem) => compareItems(scratchpadItem, item))) {
      alertError('This item is already in the scratchpad.');

      return;
    }

    setScratchpadState((prev) => {
      const newScratchpad = [...prev];
      newScratchpad.splice(lastDestIndex!, 0, item);
      return newScratchpad;
    });

    setHasModelChangedOnce(true);
    setHasModelChangedRecently(true);
  }

  function addItemToScratchpadFromDraft(sourceItemHierarchyId: string, destHierarchyId: string) {
    const rootCopy = getRootCopy();

    const srcHierarchyIdSanitized = sourceItemHierarchyId.replace(/.+?#/, '');
    const srcHierarchyIdIndices = readHierarchyId(srcHierarchyIdSanitized, true);

    const entity = getEntity(sourceItemHierarchyId, { indices: srcHierarchyIdIndices, root: rootCopy });

    if (entity?.subtype === MULTIZONE_MODULE) {
      const multizoneSrcHierarchySanitized = replaceZoneIdentifiers(sourceItemHierarchyId);

      const multizoneSrcIndices = readHierarchyId(multizoneSrcHierarchySanitized, true);

      const [, , , srcZoneIndex, srcItemIndex] = multizoneSrcIndices;

      const srcModule = entity as MultiZoneModuleContainer;

      const itemIndexToRemove = srcModule.attributes.zones![srcZoneIndex].contentIndexes![srcItemIndex];
      const itemToRemove = srcModule.collection[itemIndexToRemove] as MobileArticleItem;

      srcModule.collection.splice(itemIndexToRemove, 1); // Remove the item from the source module

      adjustContentIndexes(srcModule.attributes.zones!, itemIndexToRemove);

      const isLinkedItem = itemToRemove.type === EntityType.LinkedItem;
      const newItem = {
        ...itemToRemove,
        // Preserve subtype for LinkedItems so they can be re-identified when dropped back into a module.
        // For articles, reset to null to strip the MobileArticle subtype.
        subtype: isLinkedItem ? itemToRemove.subtype : null,
        attributes: isLinkedItem
          ? { ...itemToRemove.attributes }
          : {
              ...itemToRemove.attributes,
              size: null,
              treatment: null,
              showFlashline: null,
              visual: null,
              redFlashline: null,
              centerText: null
            }
      };

      const destHierarchyIdSanitized = destHierarchyId.replace(/.+?#/, '');
      const destHierarchyIdIndices = readHierarchyId(destHierarchyIdSanitized, true);

      const lastDestIndex = destHierarchyIdIndices.pop();

      setScratchpadState((prev) => {
        const newScratchpad = [...prev];
        newScratchpad.splice(lastDestIndex!, 0, newItem as ArticleItem);
        return newScratchpad;
      });

      setNewRoot(rootCopy);

      setHasModelChangedOnce(true);
      setHasModelChangedRecently(true);

      return;
    }

    const removedItem = removeEntity(sourceItemHierarchyId);

    addItemToScratchpad(destHierarchyId, removedItem as ArticleItem);
  }

  function swapItemsInScratchpad(srcIndex: number, destIndex: number) {
    setScratchpadState((prev) => {
      const newScratchpad = [...prev];
      const [removed] = newScratchpad.splice(srcIndex, 1);

      newScratchpad.splice(destIndex, 0, removed);

      setHasModelChangedOnce(true);
      setHasModelChangedRecently(true);
      return newScratchpad;
    });
  }

  function pruneLastStashedGroupForRemovedItem(removedId: string | undefined) {
    if (!removedId) return;
    setLastStashedGroup((prev) => {
      if (!prev) return prev;
      const nextModules: StashedModuleSnapshot[] = prev.modules
        .map((m): StashedModuleSnapshot => {
          if (m.kind === 'regular') {
            return { ...m, items: m.items.filter((it) => it.attributes.id !== removedId) };
          }
          const idx = m.collection.findIndex((it) => it.attributes.id === removedId);
          if (idx === -1) return m;
          const nextCollection = [...m.collection];
          nextCollection.splice(idx, 1);
          const nextZones = m.zones.map((z) => ({
            name: z.name,
            contentIndexes: z.contentIndexes.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))
          }));
          return { ...m, collection: nextCollection, zones: nextZones };
        })
        .filter((m) => (m.kind === 'regular' ? m.items.length > 0 : m.collection.length > 0));
      if (nextModules.length === 0) return null;
      return { ...prev, modules: nextModules };
    });
  }

  function removeItemFromScratchpad(item: ContentUnion) {
    setScratchpadState((prev) => prev.filter((scratchpadItem) => scratchpadItem.attributes.id !== item.attributes.id));
    pruneLastStashedGroupForRemovedItem(item.attributes.id);
    setHasModelChangedOnce(true);
    setHasModelChangedRecently(true);
  }

  function getAllScratchpadItemIds(): string[] {
    return scratchpadState
      .filter((item) => (item.attributes as { id?: string } | undefined)?.id)
      .map((item) => item.attributes.id);
  }

  function getScratchpad(deleteResolvedFields: boolean) {
    return scratchpadState.map((item) => ({
      ...item,
      content: deleteResolvedFields ? undefined : (item as ArticleItem).content
    }));
  }

  function stashGroupItems(entry: StashedGroupEntry) {
    const flatItems = entry.modules.flatMap((m) => getStashedItems(m));
    if (flatItems.length === 0) {
      // Nothing to stash — leave the previous restore slot (if any) untouched.
      return;
    }

    // Query and Collection items are dynamic/automatic — they cannot be persisted in the
    // scratchpad as they break page rendering. Only manual content and links are eligible.
    const scratchpadEligibleItems = flatItems.filter(
      (item) => item.type !== EntityType.Query && item.type !== EntityType.Collection
    );

    if (scratchpadEligibleItems.length > 0) {
      setScratchpadState((prev) => {
        const next = [...prev];
        scratchpadEligibleItems.forEach((item) => {
          if (!next.some((existing) => compareItems(existing, item))) {
            next.push(item);
          }
        });
        return next;
      });
      setHasModelChangedOnce(true);
      setHasModelChangedRecently(true);
    }

    setLastStashedGroup(entry);
  }

  function restoreLastStashedGroup() {
    if (!lastStashedGroup) return;
    const entry = lastStashedGroup;

    entry.childHierarchyIds.forEach((childHid) => {
      modifyEntity(childHid, (entity) => {
        // eslint-disable-next-line no-param-reassign
        (entity as Container).isGroupEnabled = true;
      });
    });

    entry.modules.forEach((m) => {
      modifyEntity(m.moduleHierarchyId, (entity) => {
        if (m.kind === 'regular') {
          // eslint-disable-next-line no-param-reassign
          (entity as ModuleContainer).collection = m.items;
          return;
        }
        const mz = entity as MultiZoneModuleContainer;
        // eslint-disable-next-line no-param-reassign
        mz.collection = m.collection;
        mz.attributes.zones?.forEach((zone) => {
          const saved = m.zones.find((z) => z.name === zone.name);
          if (saved) {
            // eslint-disable-next-line no-param-reassign
            zone.contentIndexes = [...saved.contentIndexes];
          }
        });
      });
    });

    const idsToRemove = new Set(
      entry.modules.flatMap((m) => getStashedItems(m).map((it) => it.attributes.id)).filter((id): id is string => !!id)
    );
    setScratchpadState((prev) => prev.filter((it) => !idsToRemove.has(it.attributes.id)));

    setLastStashedGroup(null);
    setHasModelChangedOnce(true);
    setHasModelChangedRecently(true);
  }

  function getLastStashedGroup() {
    return lastStashedGroup;
  }

  const CONTEXT = {
    height,
    setHeight,
    scrollTop,
    setScrollTop,
    addItemToScratchpad,
    removeItemFromScratchpad,
    addItemToScratchpadFromDraft,
    swapItemsInScratchpad,
    scratchpad: scratchpadState,
    getAllScratchpadItemIds,
    getScratchpad,
    stashGroupItems,
    restoreLastStashedGroup,
    getLastStashedGroup
  };

  return <ScratchpadContext.Provider value={CONTEXT}>{children}</ScratchpadContext.Provider>;
};
